import React, { useState, useEffect } from "react";
import Modal from "../../../components/ui/Modal";
import ImageUpload from "../../../components/ui/ImageUpload";
import Input from "../../../components/ui/Input";
import TextArea from "../../../components/ui/TextArea";
import Button from "../../../components/ui/Button";
import { Zap, ShoppingBag, X, Check } from "lucide-react";
import { mockCreator } from "../../../data/mockData";

const CreatePostModal = ({ isOpen, onClose, onSave, postToEdit }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isBoosted, setIsBoosted] = useState(false);
  const [publishDate, setPublishDate] = useState("");
  const [taggedProducts, setTaggedProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);

  const isEditMode = !!postToEdit;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        setTitle(postToEdit.title);
        setContent(postToEdit.content || "");
        setImageUrl(postToEdit.imageUrl || postToEdit.thumbnailUrl || null);
        setIsPremium(postToEdit.isPremium);
        setIsBoosted(postToEdit.isBoosted || false);
        setPublishDate(postToEdit.publishDate);
        setTaggedProducts(postToEdit.taggedProducts || []);
      } else {
        setTitle("");
        setContent("");
        setImageUrl(null);
        setIsPremium(false);
        setIsBoosted(false);
        setPublishDate(new Date().toISOString().split("T")[0]);
        setTaggedProducts([]);
      }
    }
  }, [isOpen, postToEdit, isEditMode]);

  const handleSaveClick = () => {
    if (!title) return;

    const postData = {
      id: isEditMode ? postToEdit.id : Date.now(),
      type: imageUrl ? "image" : "text",
      title,
      content,
      imageUrl,
      isPremium,
      isBoosted,
      taggedProducts,
      comments: postToEdit?.comments || 0,
      likes: postToEdit?.likes || 0,
      status: isEditMode ? postToEdit.status : "Draft",
      publishDate: publishDate,
    };
    onSave(postData);
    onClose();
  };

  const toggleProduct = (product) => {
    if (taggedProducts.find(p => p.id === product.id)) {
      setTaggedProducts(taggedProducts.filter(p => p.id !== product.id));
    } else {
      setTaggedProducts([...taggedProducts, product]);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Post" : "Create New Post"}
    >
      <div className="space-y-4">
        <ImageUpload
          label="Post Image (Optional)"
          currentImage={imageUrl}
          onUpload={(file, url) => setImageUrl(url)}
          aspectRatio="video"
        />
        <div>
          <label className="text-sm font-medium text-slate-700">Title</label>
          <Input
            placeholder="Your post title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Content</label>
          <div className="relative">
            <TextArea
                rows={6}
                placeholder="Write something amazing..."
                className="mt-1 pb-8"
                value={content}
                onChange={(e) => setContent(e.target.value)}
            />
            {/* Magic Write Button */}
            <button
                onClick={() => {
                    const prompts = [
                        "Check out my new vibes! 🎵 #Adnaki #NewMusic",
                        "Can't wait for you all to see this! 🔥 #BehindTheScenes",
                        "Feeling grateful for all the support today. 🙏 #Community",
                        "Something big is coming... stay tuned! 👀"
                    ];
                    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
                    setContent(prev => prev ? prev + "\n" + randomPrompt : randomPrompt);
                }}
                className="absolute bottom-2 right-2 text-xs font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-md flex items-center transition-colors border border-purple-200"
            >
                <Zap size={10} className="mr-1 fill-purple-600" />
                Magic Write
            </button>
          </div>
        </div>

        {/* Product Tagging Section */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <ShoppingBag size={16} /> Tag Products
            </label>
            <button 
              onClick={() => setShowProductSelector(!showProductSelector)}
              className="text-xs text-primary font-medium hover:underline"
            >
              {showProductSelector ? "Done Selecting" : "Select Products"}
            </button>
          </div>

          {showProductSelector && (
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-3 max-h-40 overflow-y-auto space-y-2">
              {mockCreator.merchandise.map(product => (
                <div 
                  key={product.id} 
                  onClick={() => toggleProduct(product)}
                  className={`flex items-center p-2 rounded cursor-pointer border ${
                    taggedProducts.find(p => p.id === product.id) 
                      ? "bg-primary/10 border-primary" 
                      : "bg-white border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border mr-3 flex items-center justify-center ${
                    taggedProducts.find(p => p.id === product.id) ? "bg-primary border-primary" : "border-slate-300"
                  }`}>
                    {taggedProducts.find(p => p.id === product.id) && <Check size={10} className="text-white" />}
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{product.name}</span>
                  <span className="text-xs font-bold text-slate-500">{product.priceETB} ETB</span>
                </div>
              ))}
            </div>
          )}

          {taggedProducts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {taggedProducts.map(product => (
                <div key={product.id} className="inline-flex items-center bg-purple-50 text-purple-700 px-2 py-1 rounded-md text-xs border border-purple-100">
                  <ShoppingBag size={12} className="mr-1" />
                  {product.name}
                  <button onClick={() => toggleProduct(product)} className="ml-2 hover:text-purple-900">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">
            Publish Date
          </label>
          <Input
            type="date"
            value={publishDate}
            onChange={(e) => setPublishDate(e.target.value)}
          />
        </div>
        
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="flex items-center">
            <input
              id="isPremium"
              type="checkbox"
              className="h-4 w-4 text-primary rounded border-slate-300 focus:ring-primary"
              checked={isPremium}
              onChange={(e) => setIsPremium(e.target.checked)}
            />
            <label htmlFor="isPremium" className="ml-2 text-sm text-slate-600">
              Subscribers Only (Premium Content)
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="isBoosted"
              type="checkbox"
              className="h-4 w-4 text-amber-500 rounded border-slate-300 focus:ring-amber-500"
              checked={isBoosted}
              onChange={(e) => setIsBoosted(e.target.checked)}
            />
            <label htmlFor="isBoosted" className="ml-2 text-sm text-slate-600 flex items-center gap-1">
              <Zap size={14} className="text-amber-500 fill-amber-500" />
              Boost this post (Reach more fans)
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-4 space-x-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveClick}>
            {isEditMode ? "Save Changes" : "Save Draft"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreatePostModal;
